/**
 * Session-scoped location state, split out of locationHeartbeat.
 *
 * The heartbeat itself is a React hook that posts fixes through apiClient, so
 * importing it drags in React and MSAL — and MSAL touches `window` at module
 * scope. Photo geotagging only needs to *read* this state, so it lives here in
 * a leaf module with no imports at all. That is what keeps photoLocation (and
 * inspectionSyncCore, which imports it) loadable in the node test environment.
 *
 * State is deliberately module-level rather than React state: it is a
 * process-wide session fact, read from non-component code paths.
 */

// Whether the user has granted location THIS session — set by an explicit
// Set-GPS capture (markLocationGranted, called from geolocation.ts) or the
// in-app permission prompt. Lets the passive tracker read location WITHOUT ever
// triggering its own OS prompt.
let sessionGranted = false;

export function markLocationGranted(): void {
  sessionGranted = true;
}

/**
 * Drop the session grant after the OS reports the permission was revoked, so
 * the tracker falls back to passive re-checking instead of looping on errors.
 */
export function clearLocationGranted(): void {
  sessionGranted = false;
}

/**
 * Has location been granted this session? Lets other passive readers (photo
 * capture) decide whether calling getCurrentPosition is safe. Calling it while
 * the permission is still 'prompt' would raise the OS dialog, and a denied
 * dialog poisons the permission for the whole origin — including Set-GPS — so
 * everything outside an explained user tap must gate on this.
 */
export function isLocationGranted(): boolean {
  return sessionGranted;
}

/** The most recent fix the tracker has seen this session, for consumers that
 *  need a position but must not trigger their own lookup. */
export interface LastKnownFix {
  lat: number;
  lng: number;
  accuracyM: number | null;
  at: number;
}

let lastKnownFix: LastKnownFix | null = null;

export function setLastKnownFix(fix: LastKnownFix): void {
  lastKnownFix = fix;
}

/**
 * Forget the last fix. Pairs with clearLocationGranted: once the OS has revoked
 * location, a position captured before the revocation should not keep being
 * handed to photo capture as if tracking were still live.
 */
export function clearLastKnownFix(): void {
  lastKnownFix = null;
}

/**
 * Last position observed by the running heartbeat, or null if it has never had
 * one. Callers should treat an old fix as suspect — during an inspection the
 * tracker updates every ~30s, so anything much older means tracking has stopped.
 */
export function getLastKnownFix(): LastKnownFix | null {
  return lastKnownFix;
}

/**
 * How long a fix stays "fresh" before we stop claiming we're tracking.
 *
 * While the watch is healthy `lastKnownFix` is refreshed constantly (every fix,
 * before any throttling), so a gap this long means the watch has gone quiet —
 * the device suspended it, the OS revoked location, or we never got a fix at
 * all. Generous enough that a stationary tech on a slow device isn't told
 * tracking stopped when it hasn't.
 */
export const LOCATION_FIX_STALE_MS = 3 * 60_000;

/**
 * What the tech is actually getting right now:
 *  - `tracking`     — permission granted AND fixes are arriving.
 *  - `acquiring`    — permission granted but no (recent) fix yet. Honest middle
 *                     state: nothing is being recorded, but a tap won't help.
 *  - `not_granted`  — location has never been allowed here; an explained,
 *                     user-tapped prompt CAN fix it.
 *  - `blocked`      — hard denied; only a device/browser settings change helps.
 */
export type LocationTrackingState = 'tracking' | 'acquiring' | 'not_granted' | 'blocked';

/**
 * Honest read of whether live tracking is actually producing data.
 *
 * Strictly passive and side-effect free: it only inspects state the tracker has
 * already gathered (`isLocationGranted`, `getLastKnownFix`) plus a permission
 * value the caller read via `checkGeolocationPermission`. It NEVER touches
 * navigator.geolocation, so it is safe to poll — polling can't raise an OS
 * prompt, and a denied prompt would poison location for the whole origin.
 */
export function readLocationTrackingState(
  permission: 'granted' | 'denied' | 'prompt' | 'unknown',
  opts: {
    blocked?: boolean;
    now?: number;
    /** Overrides for tests; production callers pass neither. */
    granted?: boolean;
    fix?: LastKnownFix | null;
  } = {},
): LocationTrackingState {
  if (opts.blocked || permission === 'denied') return 'blocked';
  // Safari reports 'unknown'; there the session flag (set by a real fix or an
  // explicit grant) is the only evidence we have.
  const granted = opts.granted ?? isLocationGranted();
  if (permission !== 'granted' && !granted) return 'not_granted';
  const fix = opts.fix !== undefined ? opts.fix : getLastKnownFix();
  const now = opts.now ?? Date.now();
  return fix && now - fix.at <= LOCATION_FIX_STALE_MS ? 'tracking' : 'acquiring';
}
